import { useCallback, useRef, useState } from 'react';
import FileSaver from 'file-saver';

import { downloadCollatedReport } from '@store/actions';

/** Parse legacy `filename=` from a Content-Disposition header value. */
function filenameFromContentDisposition(disposition: string | undefined): string | null {
  if (!disposition?.includes('filename=')) {
    return null;
  }

  const matches = disposition.match(/filename[^;=\n]*=(['"]?)([^'"\n]*)\1/);

  return matches?.[2] ? decodeURIComponent(matches[2]) : null;
}

export function useDownloadPatientCollatedReport(requisitionId: string) {
  const [isDownloadingCollatedReport, setIsDownloadingCollatedReport] = useState(false);
  const pendingRef = useRef(false);

  const onDownloadCollatedReport = useCallback(async () => {
    if (pendingRef.current) {
      return;
    }

    pendingRef.current = true;

    try {
      setIsDownloadingCollatedReport(true);

      const response = await downloadCollatedReport(requisitionId);
      const disposition = response.headers['content-disposition'];
      const fromHeader = filenameFromContentDisposition(disposition);
      const filename = fromHeader ?? 'download.pdf';

      FileSaver.saveAs(response.data, filename);
    } catch (err) {
      console.error('Error downloading collated report: ', err);
    } finally {
      setIsDownloadingCollatedReport(false);
      pendingRef.current = false;
    }
  }, [requisitionId]);

  return { isDownloadingCollatedReport, onDownloadCollatedReport };
}
